import { MouseEvent } from 'react';
import {
    Stack,
    Box,
    Typography,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    ToggleButtonGroup,
    ToggleButton,
    Alert,
    Button,
    Paper,
    IconButton,
    Divider,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useI18nContext } from '@/i18n/i18n-react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { updateFormData } from '@/store/installSlice';

const WEEKDAYS = [
    { value: 1, label: 'Mon' },
    { value: 2, label: 'Tue' },
    { value: 3, label: 'Wed' },
    { value: 4, label: 'Thu' },
    { value: 5, label: 'Fri' },
    { value: 6, label: 'Sat' },
    { value: 7, label: 'Sun' },
];

interface EventConfig {
    name: string;
    location: string;
    startTime: string;
    endTime: string;
    recurrenceType: 'weekly' | 'monthly';
    weekdays: number[];
    monthWeek: number;
    monthDay: number;
}

export const CalendarStep = () => {
    const { LL } = useI18nContext();
    const dispatch = useAppDispatch();
    const formData = useAppSelector((state) => state.install.formData);

    // Initialize events array if not set
    const events: EventConfig[] = formData.events || [
        {
            name: 'Service',
            location: 'Church',
            startTime: '09:30:00',
            endTime: '12:00:00',
            recurrenceType: 'weekly',
            weekdays: [7],
            monthWeek: 1,
            monthDay: 7,
        },
    ];

    const handleEventChange = (index: number, field: keyof EventConfig, value: any) => {
        const updatedEvents = [...events];
        updatedEvents[index] = { ...updatedEvents[index], [field]: value };
        dispatch(updateFormData({ events: updatedEvents }));
    };

    const handleWeekdaysChange =
        (index: number) => (_: MouseEvent<HTMLElement>, newValue: number[]) => {
            if (newValue.length > 0) {
                handleEventChange(index, 'weekdays', newValue);
            }
        };

    const addEvent = () => {
        const newEvent: EventConfig = {
            name: 'New Event',
            location: '',
            startTime: '10:00:00',
            endTime: '12:00:00',
            recurrenceType: 'weekly',
            weekdays: [7],
            monthWeek: 1,
            monthDay: 7,
        };
        dispatch(updateFormData({ events: [...events, newEvent] }));
    };

    const removeEvent = (index: number) => {
        if (events.length > 1) {
            const updatedEvents = events.filter((_, i) => i !== index);
            dispatch(updateFormData({ events: updatedEvents }));
        }
    };

    return (
        <Stack spacing={3}>
            <Box>
                <Typography variant="h6" gutterBottom>
                    Event Configuration
                </Typography>
                <Typography
                    variant="body2"
                    sx={{
                        color: 'text.secondary',
                        mb: 2,
                    }}
                >
                    {LL.install.calendar.description()}
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                    Configure your event schedule. You can add multiple events with different
                    recurrence patterns. Events can be modified or extended later in the Admin
                    panel. ICS calendar export will be automatically enabled.
                </Alert>
            </Box>
            {events.map((event, index) => (
                <Paper
                    key={index}
                    elevation={2}
                    sx={{
                        p: 3,
                        border: '1px solid',
                        borderColor: 'divider',
                        position: 'relative',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ flex: 1 }}>
                            Event {index + 1}
                            {event.name && `: ${event.name}`}
                        </Typography>
                        {events.length > 1 && (
                            <IconButton
                                color="error"
                                onClick={() => removeEvent(index)}
                                size="small"
                                aria-label="Remove event"
                            >
                                <DeleteIcon />
                            </IconButton>
                        )}
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    <Stack spacing={2}>
                        <TextField
                            label={LL.install.calendar.eventName()}
                            placeholder={LL.install.calendar.eventNamePlaceholder()}
                            value={event.name}
                            onChange={(e) => handleEventChange(index, 'name', e.target.value)}
                            fullWidth
                            required
                            size="small"
                            helperText="Name of your event (e.g., 'Sunday Service', 'Prayer Meeting')"
                        />

                        <TextField
                            label={LL.install.calendar.eventLocation()}
                            placeholder={LL.install.calendar.eventLocationPlaceholder()}
                            value={event.location}
                            onChange={(e) => handleEventChange(index, 'location', e.target.value)}
                            fullWidth
                            size="small"
                            helperText="Where does the event take place?"
                        />

                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label={LL.install.calendar.startTime()}
                                type="time"
                                value={event.startTime.slice(0, 5)}
                                onChange={(e) =>
                                    handleEventChange(index, 'startTime', e.target.value + ':00')
                                }
                                fullWidth
                                required
                                size="small"
                                slotProps={{ inputLabel: { shrink: true } }}
                            />

                            <TextField
                                label={LL.install.calendar.endTime()}
                                type="time"
                                value={event.endTime.slice(0, 5)}
                                onChange={(e) =>
                                    handleEventChange(index, 'endTime', e.target.value + ':00')
                                }
                                fullWidth
                                required
                                size="small"
                                slotProps={{ inputLabel: { shrink: true } }}
                            />
                        </Box>

                        <FormControl fullWidth size="small">
                            <InputLabel>Recurrence Type</InputLabel>
                            <Select
                                value={event.recurrenceType}
                                label="Recurrence Type"
                                onChange={(e) =>
                                    handleEventChange(
                                        index,
                                        'recurrenceType',
                                        e.target.value as 'weekly' | 'monthly'
                                    )
                                }
                            >
                                <MenuItem value="weekly">Weekly</MenuItem>
                                <MenuItem value="monthly">Monthly</MenuItem>
                            </Select>
                        </FormControl>

                        {event.recurrenceType === 'weekly' && (
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    Select Days of Week
                                </Typography>
                                <ToggleButtonGroup
                                    value={event.weekdays}
                                    onChange={handleWeekdaysChange(index)}
                                    aria-label="weekdays"
                                    sx={{ flexWrap: 'wrap', gap: 0.5 }}
                                    size="small"
                                >
                                    {WEEKDAYS.map((day) => (
                                        <ToggleButton
                                            key={day.value}
                                            value={day.value}
                                            aria-label={day.label}
                                            sx={{ px: 2 }}
                                        >
                                            {day.label}
                                        </ToggleButton>
                                    ))}
                                </ToggleButtonGroup>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: 'text.secondary',
                                        mt: 1,
                                        display: 'block',
                                    }}
                                >
                                    Select one or more days when the event occurs
                                </Typography>
                            </Box>
                        )}

                        {event.recurrenceType === 'monthly' && (
                            <Box>
                                <Typography variant="subtitle2" gutterBottom>
                                    Monthly Recurrence Pattern
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Week of Month</InputLabel>
                                        <Select
                                            value={event.monthWeek}
                                            label="Week of Month"
                                            onChange={(e) =>
                                                handleEventChange(
                                                    index,
                                                    'monthWeek',
                                                    e.target.value as number
                                                )
                                            }
                                        >
                                            <MenuItem value={1}>First</MenuItem>
                                            <MenuItem value={2}>Second</MenuItem>
                                            <MenuItem value={3}>Third</MenuItem>
                                            <MenuItem value={4}>Fourth</MenuItem>
                                            <MenuItem value={5}>Last</MenuItem>
                                        </Select>
                                    </FormControl>

                                    <FormControl fullWidth size="small">
                                        <InputLabel>Day of Week</InputLabel>
                                        <Select
                                            value={event.monthDay}
                                            label="Day of Week"
                                            onChange={(e) =>
                                                handleEventChange(
                                                    index,
                                                    'monthDay',
                                                    e.target.value as number
                                                )
                                            }
                                        >
                                            <MenuItem value={1}>Monday</MenuItem>
                                            <MenuItem value={2}>Tuesday</MenuItem>
                                            <MenuItem value={3}>Wednesday</MenuItem>
                                            <MenuItem value={4}>Thursday</MenuItem>
                                            <MenuItem value={5}>Friday</MenuItem>
                                            <MenuItem value={6}>Saturday</MenuItem>
                                            <MenuItem value={7}>Sunday</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: 'text.secondary',
                                        mt: 1,
                                        display: 'block',
                                    }}
                                >
                                    Example: "First Sunday" or "Last Friday" of each month
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                </Paper>
            ))}
            <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addEvent}
                fullWidth
                sx={{ mt: 2 }}
            >
                Add Another Event
            </Button>
            <Typography
                variant="caption"
                sx={{
                    color: 'text.secondary',
                    textAlign: 'center',
                }}
            >
                ICS calendar export is automatically enabled for all events
            </Typography>
        </Stack>
    );
};
